import type { ChangeEvent } from 'react'
import { useCallback, useContext, useMemo, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineBugIcon from 'LuxKit/Icons/line/LineBugIcon'
import LineChatBubbleUserIcon from 'LuxKit/Icons/line/LineChatBubbleUserIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ContactContext from 'contexts/contactContext'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import type { TripPlannerChat } from 'tripPlanner/api/types'
import type { BasicTrip } from 'tripPlanner/types/common'
import Switch from 'LuxKit/Switch/Switch'
import PromptSelector, { type PromptOption } from './PromptSelector'
import DrawerChatListItem from './DrawerChatListItem'
import DrawerTripListItem from 'tripPlanner/pages/chat/DrawerTripListItem'
import Sheet from 'components/utils/Sheet'
import useChatTripPlannerReleaseState from 'tripPlanner/hooks/useChatTripPlannerReleaseState'

const FEEDBACK_SURVEY_URL = 'https://usabi.li/do/gizugyzacokd/vj9hxb'
const INITIAL_VISIBLE_CHATS = 3
const CHATS_INCREMENT = 5
const INITIAL_VISIBLE_TRIPS = 3
const TRIPS_INCREMENT = 5

const HeaderLogoWrapper = styled.div`
  display: flex;
  align-items: center;
`

const EMPTY_PROMPTS: Array<PromptOption> = []

interface Props {
  onStartNewChat: () => void
  recentChats?: Array<TripPlannerChat>
  recentTrips?: Array<BasicTrip>
  onChatSelect?: (chatId: number) => void
  onChatDelete?: (chatId: number) => void
  onTripDelete?: (tripId: string) => void
  // Staff tools
  isStaff?: boolean
  isDevMode?: boolean
  onDevModeToggle?: () => void
  canManagePrompts?: boolean
  prompts?: Array<PromptOption>
  selectedPromptKey?: string
  onPromptSelect?: (key: string) => void
  onPromptAdd?: () => void
}

function TripPlannerDrawer({
  onStartNewChat,
  recentChats,
  recentTrips,
  onChatSelect,
  onChatDelete,
  onTripDelete,
  isStaff = false,
  isDevMode = false,
  onDevModeToggle,
  canManagePrompts = false,
  prompts = EMPTY_PROMPTS,
  selectedPromptKey,
  onPromptSelect,
  onPromptAdd,
}: Props) {
  const intl = useIntl()
  const { resolve } = useModalElementContext()
  const supportContact = useContext(ContactContext)
  const [visibleChatsCount, setVisibleChatsCount] = useState(INITIAL_VISIBLE_CHATS)
  const [visibleTripsCount, setVisibleTripsCount] = useState(INITIAL_VISIBLE_TRIPS)
  const { shouldUseChatTripPlannerDirectEntryPoint } =
    useChatTripPlannerReleaseState()

  const closeDrawer = useCallback(() => {
    resolve()
  }, [resolve])

  const handleNewChat = useCallback(() => {
    closeDrawer()
    onStartNewChat()
  }, [closeDrawer, onStartNewChat])

  const handleChatClick = useCallback((chatId: number) => () => {
    closeDrawer()
    onChatSelect?.(chatId)
  }, [closeDrawer, onChatSelect])

  const handleDeleteClick = useCallback(
    (chatId: number, closeMenu: () => void) => (event: React.MouseEvent) => {
      event.stopPropagation()
      closeMenu()
      closeDrawer()
      onChatDelete?.(chatId)
    },
    [closeDrawer, onChatDelete],
  )

  const handleTripDeleteClick = useCallback(
    (tripId: string, closeMenu: () => void) => (event: React.MouseEvent) => {
      event.stopPropagation()
      closeMenu()
      closeDrawer()
      onTripDelete?.(tripId)
    },
    [closeDrawer, onTripDelete],
  )

  const handleDevModeToggle = useCallback((_event: ChangeEvent<HTMLInputElement>) => {
    closeDrawer()
    onDevModeToggle?.()
  }, [closeDrawer, onDevModeToggle])

  const handlePromptSelect = useCallback((promptKey: string) => {
    onPromptSelect?.(promptKey)
  }, [onPromptSelect])

  const handleAddPrompt = useCallback(() => {
    onPromptAdd?.()
  }, [onPromptAdd])

  const handleShowMoreChats = useCallback(() => {
    setVisibleChatsCount(prevCount => prevCount + CHATS_INCREMENT)
  }, [])

  const handleShowMoreTrips = useCallback(() => {
    setVisibleTripsCount(prevCount => prevCount + TRIPS_INCREMENT)
  }, [])

  const allChats = recentChats && recentChats.length > 0 ? recentChats : []
  const displayedChats = allChats.slice(0, visibleChatsCount)
  const hasMoreChats = allChats.length > visibleChatsCount
  const hasChats = displayedChats.length > 0

  const allTrips = recentTrips && recentTrips.length > 0 ? recentTrips : []
  const displayedTrips = allTrips.slice(0, visibleTripsCount)
  const hasMoreTrips = allTrips.length > visibleTripsCount
  const hasTrips = displayedTrips.length > 0
  const chatsLabel = intl.formatMessage({
    id: 'x3JBSvTY-jXIoAlF0Kiw',
    defaultMessage: 'Chats',
    description: 'Section heading for the chats list in the trip planner drawer',
  })
  const createNewChatLabel = intl.formatMessage({
    id: 'hMLFrteXDG4txmeo29R7',
    defaultMessage: 'Create new chat',
    description: 'Button label to start a new chat in the trip planner drawer',
  })
  const seeMoreLabel = intl.formatMessage({
    id: 'mhGzTTMyTy2SyKLhvTy5',
    defaultMessage: 'See more',
    description: 'Button label to show more items in a list in the trip planner drawer',
  })
  const planWithAiChatHeading = intl.formatMessage({
    id: 'LBjr7kIwyT6PI1I_-koY',
    defaultMessage: 'Plan with AI chat',
    description: 'Heading for the empty state when no chats exist in the drawer',
  })
  const planWithAiChatBody = intl.formatMessage({
    id: 'w3ssyhVL6Z7hEsPnhNhc',
    defaultMessage: 'Explore ideas, shape your trip, and refine plans as you go.',
    description: 'Description for the empty state when no chats exist in the drawer',
  })
  const tripPlansLabel = intl.formatMessage({
    id: 'kllKGGkSAAN4MRsQ774n',
    defaultMessage: 'Trip plans',
    description: 'Section heading for the trip plans list in the trip planner drawer',
  })
  const createNewTripLabel = intl.formatMessage({
    id: 'F9HlW3kzio6PKep9IBvv',
    defaultMessage: 'Create new trip',
    description: 'Button label to create a new trip in the trip planner drawer',
  })
  const createATripHeading = intl.formatMessage({
    id: '8eSKlYRYj6X7bLzlg5Az',
    defaultMessage: 'Create a trip',
    description: 'Heading for the empty state when no trips exist in the drawer',
  })
  const createATripBody = intl.formatMessage({
    id: 'skzDiRo2ef1Kqpnd0HM5',
    defaultMessage: 'Create a day-by-day plan for your trip.',
    description: 'Description for the empty state when no trips exist in the drawer',
  })

  const giveFeedbackTitle = intl.formatMessage({
    id: 'XQlLVgnHoOF0Vvfrs3F5',
    defaultMessage: 'Give feedback',
    description: 'Title for the give feedback link in the TripPlanner navigation drawer menu',
  })

  const giveFeedbackSubtitle = intl.formatMessage({
    id: 'EFHzgukGnljGQ2-yKUwj',
    defaultMessage: 'We want to hear your thoughts on TripPlanner',
    description: 'Subtitle encouraging users to share feedback about the TripPlanner feature',
  })

  const needSupportTitle = intl.formatMessage({
    id: 'FbRO4l6WxkGkA3OmUAPm',
    defaultMessage: 'Need support? Call us 24/7',
    description: 'Title for the customer support link in the TripPlanner navigation drawer',
  })

  const staffToolsText = intl.formatMessage({
    id: 'MVS8v_NHiDbWbR0NuItv',
    defaultMessage: 'Staff tools',
    description: 'Label for the staff-only tools section in the TripPlanner navigation drawer',
  })

  const devModeText = intl.formatMessage({
    id: 'dXMxtVabgKHFm0Fe0oMH',
    defaultMessage: 'Dev mode',
    description: 'Toggle label for enabling developer mode in the TripPlanner navigation drawer',
  })

  const promptLabelText = intl.formatMessage({
    id: 'rUYdnoFdjtyshZb_MbFZ',
    defaultMessage: 'Prompt',
    description: 'Label for the prompt selector section in the TripPlanner navigation drawer',
  })

  const headerTitle = useMemo(() => (
    <HeaderLogoWrapper>
      <Clickable to="">
        <LogoIcon height={24} desktopVariant="icon+text" />
      </Clickable>
    </HeaderLogoWrapper>
  ), [])

  return (
    <ModalBase mode="menu">
      <ModalHeader title={headerTitle} onCloseButtonClick={closeDrawer} />
      <ModalBody>
        <ModalContent flushListItems>
          <Group direction="vertical" gap={28}>
            <Group direction="vertical">
              <Group direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="center">
                <BodyText variant="small" colour="neutral-three">
                  {chatsLabel}
                </BodyText>
                <TextButton kind="tertiary" size="small" onClick={handleNewChat}>
                  {createNewChatLabel}
                </TextButton>
              </Group>

              {hasChats && (
                <Group direction="vertical" gap={0}>
                  {displayedChats.map(chat => (
                    <DrawerChatListItem
                      key={chat.id}
                      chat={chat}
                      onChatClick={handleChatClick}
                      onDeleteClick={handleDeleteClick}
                    />
                  ))}
                  {hasMoreChats && (
                    <ClickableListItem
                      title={seeMoreLabel}
                      endIcon={<LineAngleDownIcon />}
                      size="medium"
                      onClick={handleShowMoreChats}
                    />
                  )}
                </Group>
              )}
              {!hasChats && (
                <Sheet padding="12 20" background="neutral-seven" borderRadius="M">
                  <Group direction="vertical" gap={2}>
                    <BodyText variant="medium" weight="bold">
                      {planWithAiChatHeading}
                    </BodyText>
                    <BodyText variant="small" colour="neutral-two">
                      {planWithAiChatBody}
                    </BodyText>
                  </Group>
                </Sheet>
              )}
            </Group>
            {!shouldUseChatTripPlannerDirectEntryPoint && (
              <Group direction="vertical">
                <Group direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="center">
                  <BodyText variant="small" colour="neutral-three">
                    {tripPlansLabel}
                  </BodyText>
                  <TextButton kind="tertiary" size="small" to="/trip-planner/generate-trip" onClick={closeDrawer}>
                    {createNewTripLabel}
                  </TextButton>
                </Group>
                {hasTrips && (
                  <Group direction="vertical" gap={0}>
                    {displayedTrips.map(trip => (
                      <DrawerTripListItem
                        key={trip.id}
                        trip={trip}
                        onClick={closeDrawer}
                        onDeleteClick={handleTripDeleteClick}
                      />
                    ))}
                    {hasMoreTrips && (
                      <ClickableListItem
                        title={seeMoreLabel}
                        endIcon={<LineAngleDownIcon />}
                        size="medium"
                        onClick={handleShowMoreTrips}
                      />
                    )}
                  </Group>
                )}
                {!hasTrips && (
                  <Sheet padding="12 20" background="neutral-seven" borderRadius="M">
                    <Group direction="vertical" gap={2}>
                      <BodyText variant="medium" weight="bold">
                        {createATripHeading}
                      </BodyText>
                      <BodyText variant="small" colour="neutral-two">
                        {createATripBody}
                      </BodyText>
                    </Group>
                  </Sheet>
                )}
              </Group>
            )}

            <Group direction="vertical" gap={8}>
              <ClickableListItem
                title={giveFeedbackTitle}
                subtitle={giveFeedbackSubtitle}
                startIcon={<LineChatBubbleUserIcon />}
                href={FEEDBACK_SURVEY_URL}
                target="_blank"
                rel="noopener noreferrer"
              />
              <ClickableListItem
                title={needSupportTitle}
                subtitle={supportContact.local.humanReadable}
                startIcon={<LinePhoneIcon />}
                href={`tel:${supportContact.local.number}`}
              />
            </Group>
          </Group>
          {isStaff && (
            <Group direction="vertical" gap={12}>
              <BodyText variant="medium" weight="bold">{staffToolsText}</BodyText>

              <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
                <Group direction="horizontal" gap={8} verticalAlign="center">
                  <LineBugIcon />
                  <BodyText variant="medium">{devModeText}</BodyText>
                </Group>
                <Switch
                  checked={isDevMode}
                  onChange={handleDevModeToggle}
                />
              </Group>

              {canManagePrompts && prompts.length > 0 && selectedPromptKey && (
                <Group direction="vertical" gap={8}>
                  <BodyText variant="small" colour="neutral-three">{promptLabelText}</BodyText>
                  <PromptSelector
                    prompts={prompts}
                    selectedPromptKey={selectedPromptKey}
                    onSelect={handlePromptSelect}
                    onAddPrompt={handleAddPrompt}
                  />
                </Group>
              )}
            </Group>
          )}
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default TripPlannerDrawer
